import * as THREE from "three";

/**
 * A halo ring positioned at the ship that brightens + grows + spins with the
 * current streak. Lives in scene-space (not parented to ship) so the camera
 * trauma perturbation doesn't double-apply. Cheap: one mesh, one material.
 */
export class StreakAura {
  readonly mesh: THREE.Mesh;
  private mat: THREE.MeshBasicMaterial;
  private targetScale = 1;
  private targetOpacity = 0;
  private spin = 0;
  private color = new THREE.Color();

  constructor() {
    const geo = new THREE.RingGeometry(0.95, 1.2, 64);
    this.mat = new THREE.MeshBasicMaterial({
      color: 0x00e0ff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(geo, this.mat);
    this.mesh.rotation.x = Math.PI / 2;
  }

  /**
   * Per-frame update. `streak >= 5` lights the halo; below that it fades.
   * Hue shifts cyan → magenta with streak, mirroring the ribbon trail.
   */
  update(dt: number, shipPos: THREE.Vector3, streak: number) {
    this.mesh.position.copy(shipPos);
    this.mesh.position.z += 0.1; // sit just behind the ship glow disc
    // Under reduced-motion, freeze the spin so it reads as a static halo.
    const reduced = typeof document !== "undefined" &&
      document.body.classList.contains("reduced-motion");
    if (!reduced) {
      this.spin += dt * (0.4 + Math.min(streak, 30) * 0.05);
    }
    this.mesh.rotation.z = this.spin;

    const t = Math.min(1, Math.max(0, (streak - 5) / 25));
    this.targetScale = 1 + t * 1.0;
    this.targetOpacity = streak >= 5 ? 0.35 + t * 0.5 : 0;
    const h = 0.50 + t * 0.32;
    this.color.setHSL(h, 0.9, 0.6);
    this.mat.color.lerp(this.color, 1 - Math.pow(0.001, dt));

    const k = 1 - Math.pow(0.001, dt);
    this.mesh.scale.setScalar(THREE.MathUtils.lerp(this.mesh.scale.x, this.targetScale, k));
    this.mat.opacity = THREE.MathUtils.lerp(this.mat.opacity, this.targetOpacity, k);
  }
}
